import React, {
  FC,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Claim, Entitlement, EntitlementProcess } from '../../types/entitlements';
import {
  IEmsAuditActorResponse,
  IEmsAuditEvent,
  IEntitlementDeletedAuditEvent,
  IEntitlementUpsertedAuditEvent,
} from '../../types/audit-event';
import { UseCollectionHookResult } from '../../hooks/useCollection';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useKeysetData } from '../../hooks/useKeysetData';
import { entitlementsService } from '../../services/entitlementService';
import { EntitlementRowData } from './ClaimsTable/entitlements-ui-types';
import {
  EntitlementDiffView,
  EntitlementEventDetailDialog,
  EntitlementSnapshotView,
  formatDate,
} from '../EntitlementEventDetailDialog';
import { isDateInFuture, isDateInPast } from '../../util/subscribedAccessUtils';
import { useAuth } from '../../providers/AuthProvider';
import { isEditableEntitlement } from '../../util/utils';
import TableErrorComponent from '../General/TableErrorComponent';

interface EntitlementsEnhancedDataGridProps {
  collectionHook: UseCollectionHookResult<Entitlement>;
  onEdit: (data: EntitlementRowData) => void;
  identityId: string;
  onRefreshCurrent: () => void;
}

// --- Key helpers ---

const normKey = (v: string | null | undefined) => (v ?? '').toLowerCase().trim();
const toPascalCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
const entitlementKey = (
  p: string | null | undefined,
  s: string | null | undefined,
  id: string | null | undefined
) => `${normKey(p)}|${normKey(s)}|${normKey(id)}`;

// --- Helpers ---

/** If a user actor exists anywhere in the chain as a sub-actor, surface it as the
 *  primary display actor (the system/support actor becomes the "via" context). */
function resolveDisplayActor(raw: IEmsAuditActorResponse | null | undefined): {
  display: IEmsAuditActorResponse | null;
  via: IEmsAuditActorResponse | null;
} {
  if (!raw) return { display: null, via: null };
  if (raw.type === 'auditing.event.actor.user.v1') return { display: raw, via: raw.parentActor };
  // Walk the chain looking for a user actor
  let current: IEmsAuditActorResponse | null = raw.parentActor;
  while (current) {
    if (current.type === 'auditing.event.actor.user.v1') return { display: current, via: raw };
    current = current.parentActor;
  }
  return { display: raw, via: raw.parentActor };
}

const ACTOR_TYPE_CONFIG: Record<string, { icon: React.ReactElement; label: string }> = {
  'auditing.event.actor.user.v1': { icon: <PersonOutlinedIcon fontSize="small" />, label: 'User' },
  'auditing.event.actor.system.v1': {
    icon: <SmartToyOutlinedIcon fontSize="small" />,
    label: 'System',
  },
  'auditing.event.actor.customersupport.v1': {
    icon: <SupportAgentOutlinedIcon fontSize="small" />,
    label: 'Customer support',
  },
};

interface ClaimStatus {
  label: string;
  color: 'success' | 'error' | 'warning' | 'info' | 'default';
}

function getClaimStatus(claim: Claim): ClaimStatus {
  if (!claim.isActive) return { label: 'Inactive', color: 'default' };
  if (claim.validFrom && isDateInFuture(claim.validFrom))
    return { label: 'Upcoming', color: 'info' };
  if (claim.validTo && isDateInPast(claim.validTo)) return { label: 'Expired', color: 'error' };
  return { label: 'Active', color: 'success' };
}

function getClaimsSummary(claims: Claim[] | null | undefined) {
  if (!claims || claims.length === 0) return { total: 0, active: 0 };
  const active = claims.filter((c) => {
    if (!c.isActive) return false;
    if (c.validFrom && isDateInFuture(c.validFrom)) return false;
    if (c.validTo && isDateInPast(c.validTo)) return false;
    return true;
  }).length;
  return { total: claims.length, active };
}

function summarizeUpsert(event: IEntitlementUpsertedAuditEvent): string {
  const old = event.oldEntitlement;
  const next = event.newEntitlement;
  if (!old && next) return 'Entitlement created';
  if (old && !next) return 'Entitlement cleared';
  if (!old && !next) return 'No data';

  const oldClaims = old!.claims ?? [];
  const newClaims = next!.claims ?? [];
  const oldKeys = new Set(oldClaims.map((c) => `${c.brand}|${c.code}`));
  const newKeys = new Set(newClaims.map((c) => `${c.brand}|${c.code}`));

  const added = newClaims.filter((c) => !oldKeys.has(`${c.brand}|${c.code}`)).length;
  const removed = oldClaims.filter((c) => !newKeys.has(`${c.brand}|${c.code}`)).length;
  const modified = newClaims.filter((c) => {
    const key = `${c.brand}|${c.code}`;
    if (!oldKeys.has(key)) return false;
    const oldC = oldClaims.find((oc) => `${oc.brand}|${oc.code}` === key);
    return JSON.stringify(oldC) !== JSON.stringify(c);
  }).length;

  const oldRolesSet = new Set(old!.roles ?? []);
  const newRolesSet = new Set(next!.roles ?? []);
  const allRoles = new Set([...Array.from(oldRolesSet), ...Array.from(newRolesSet)]);
  const rolesChanged = Array.from(allRoles).some((r) => oldRolesSet.has(r) !== newRolesSet.has(r));

  const parts: string[] = [];
  if (added > 0) parts.push(`+${added} claim${added > 1 ? 's' : ''}`);
  if (removed > 0) parts.push(`−${removed} claim${removed > 1 ? 's' : ''}`);
  if (modified > 0) parts.push(`~${modified} modified`);
  if (rolesChanged) parts.push('roles changed');
  return parts.length > 0 ? parts.join(' · ') : 'No changes';
}

function summarizeEvent(event: IEmsAuditEvent): string {
  if (event.type === 'auditing.event.entitlement.deleted.v1') return 'Entitlement deleted';
  return summarizeUpsert(event as IEntitlementUpsertedAuditEvent);
}

function mapToRowData(entitlement: Entitlement): EntitlementRowData {
  return {
    ...entitlement,
    ...entitlement.id,
    claims: (entitlement.claims ?? []).map((c) => {
      const invalidityReason = isDateInFuture(c.validFrom)
        ? 'upcoming'
        : isDateInPast(c.validTo)
          ? 'expired'
          : undefined;
      return { ...c, invalidityReason };
    }),
    earliestValidFrom: undefined,
    latestValidTo: undefined,
  };
}

// --- Column header with info tooltip ---

const ColHeader: FC<{ label: string; tip: string }> = ({ label, tip }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <span>{label}</span>
    <Tooltip title={tip} placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
    </Tooltip>
  </Stack>
);

// --- Claims tab ---

const ClaimsTab: FC<{ claims: Claim[] | null | undefined }> = ({ claims }) => {
  if (!claims || claims.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" p={2}>
        No claims
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: 'grey.50' }}>
          <TableCell>Brand</TableCell>
          <TableCell>Code</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Valid from</TableCell>
          <TableCell>Valid to</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {claims.map((claim) => {
          const status = getClaimStatus(claim);
          return (
            <TableRow key={`${claim.brand}|${claim.code}`}>
              <TableCell>{claim.brand ?? '-'}</TableCell>
              <TableCell>{claim.code ?? '-'}</TableCell>
              <TableCell>
                <Chip label={status.label} color={status.color} size="small" />
              </TableCell>
              <TableCell>{formatDate(claim.validFrom)}</TableCell>
              <TableCell>{formatDate(claim.validTo)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// --- History tab ---

interface HistoryTabProps {
  identityId: string;
  process: string;
  system: string;
  sourceId: string;
  onViewDetail: (event: IEmsAuditEvent) => void;
}

interface HistoryTabHandle {
  refresh: () => void;
}

const PAGE_SIZE_OPTIONS = [5, 10, 20] as const;

const HistoryTab = forwardRef<HistoryTabHandle, HistoryTabProps>(
  ({ identityId, process, system, sourceId, onViewDetail }, ref) => {
    const pagination = useCursorPagination(10);
    const {
      items: events,
      loading,
      error,
      hasNext,
      totalCount,
      refetch,
    } = useKeysetData(
      () =>
        entitlementsService.getAuditEventsForEntitlement(
          identityId,
          process,
          system,
          sourceId,
          pagination.pageSize,
          pagination.cursor
        ),
      [identityId, process, system, sourceId, pagination.pageSize, pagination.cursor]
    );
    const [showNoChanges, setShowNoChanges] = useState(false);
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({ refresh: refetch }), [refetch]);

    const visibleEvents = useMemo(
      () =>
        showNoChanges
          ? events
          : events.filter(
              (e) =>
                e.type !== 'auditing.event.entitlement.upserted.v1' ||
                summarizeUpsert(e as IEntitlementUpsertedAuditEvent) !== 'No changes'
            ),
      [events, showNoChanges]
    );

    const hiddenCount = events.length - visibleEvents.length;
    const from = events.length > 0 ? pagination.page * pagination.pageSize + 1 : 0;
    const to = pagination.page * pagination.pageSize + events.length;

    return (
      <>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          px={2}
          py={0.75}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            Only shows events recorded after the introduction of entitlement audit logging.
          </Typography>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell padding="checkbox" />
              <TableCell>Changes</TableCell>
              <TableCell>Performed by</TableCell>
              <TableCell>Date</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {error && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="error" py={2}>
                    Failed to load history.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <Skeleton variant="text" width={160} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={130} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={110} />
                  </TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              ))}
            {!loading && !error && events.length === 0 && pagination.page === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No events found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !error &&
              visibleEvents.map((event) => {
                const { display: actor, via: viaActor } = resolveDisplayActor(event.performedBy);
                const actorConfig = actor?.type ? ACTOR_TYPE_CONFIG[actor.type] : undefined;
                const actorLabel = actor?.name ?? actor?.id ?? '-';
                const isExpanded = expandedEventId === event.id;

                return (
                  <React.Fragment key={event.id}>
                    <TableRow
                      hover
                      sx={{ '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => setExpandedEventId(isExpanded ? null : event.id)}>
                          {isExpanded ? (
                            <KeyboardArrowDownOutlinedIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            event.type === 'auditing.event.entitlement.deleted.v1'
                              ? 'error'
                              : 'text.secondary'
                          }>
                          {summarizeEvent(event)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {actorConfig ? (
                          <Tooltip
                            title={`${actorConfig.label}: ${actorLabel}${viaActor ? ` (via ${viaActor.name ?? viaActor.id ?? 'unknown'})` : ''}`}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {actorConfig.icon}
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {actor?.type === 'auditing.event.actor.system.v1'
                                  ? 'System'
                                  : actorLabel}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">{actorLabel}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(event.dateOccurred)}
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => onViewDetail(event)}>
                            <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: 'grey.50',
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }}>
                            {event.type === 'auditing.event.entitlement.upserted.v1' ? (
                              <EntitlementDiffView
                                oldSnap={(event as IEntitlementUpsertedAuditEvent).oldEntitlement}
                                newSnap={(event as IEntitlementUpsertedAuditEvent).newEntitlement}
                                hideIdentity
                              />
                            ) : (
                              <EntitlementSnapshotView
                                snapshot={
                                  (event as IEntitlementDeletedAuditEvent).deletedEntitlement
                                }
                                hideIdentity
                              />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            {!loading && !error && events.length > 0 && visibleEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    All events hidden (no changes). Toggle switch to show.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={0.75}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showNoChanges}
                onChange={(e) => setShowNoChanges(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Show no-change events{hiddenCount > 0 && !showNoChanges ? ` (${hiddenCount})` : ''}
              </Typography>
            }
            labelPlacement="start"
            sx={{ ml: 0, mr: 0, gap: 0.5 }}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              size="small"
              value={pagination.pageSize}
              onChange={(e) => pagination.handlePageSizeChange(Number(e.target.value))}
              sx={{ fontSize: '0.75rem', height: 28 }}>
              {PAGE_SIZE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>
                  {s}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary">
              {events.length > 0
                ? `${from}–${to} of ${totalCount}${hiddenCount > 0 && !showNoChanges ? ` (${hiddenCount} hidden)` : ''}`
                : '—'}
            </Typography>
            <Button
              size="small"
              disabled={pagination.page === 0}
              onClick={() => pagination.handlePageChange(pagination.page - 1, events)}
              startIcon={<NavigateBeforeOutlinedIcon />}
              sx={{ minWidth: 0, px: 1 }}>
              Prev
            </Button>
            <Button
              size="small"
              disabled={!hasNext}
              onClick={() => pagination.handlePageChange(pagination.page + 1, events)}
              endIcon={<NavigateNextOutlinedIcon />}
              sx={{ minWidth: 0, px: 1 }}>
              Next
            </Button>
          </Stack>
        </Stack>
      </>
    );
  }
);
HistoryTab.displayName = 'HistoryTab';

// --- Claims quick view ---

const QUICK_VIEW_MAX = 10;

const ClaimsQuickView: FC<{ claims: Claim[] | null | undefined }> = ({ claims }) => {
  if (!claims || claims.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No claims
      </Typography>
    );
  }
  const visible = claims.slice(0, QUICK_VIEW_MAX);
  const overflow = claims.length - visible.length;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.5 }}>
      {visible.map((claim) => {
        const status = getClaimStatus(claim);
        const iconColor = status.color === 'default' ? 'text.disabled' : `${status.color}.main`;
        const StatusIcon =
          status.color === 'success'
            ? CheckCircleOutlinedIcon
            : status.color === 'info'
              ? ScheduleOutlinedIcon
              : status.color === 'error'
                ? CancelOutlinedIcon
                : RemoveCircleOutlinedIcon;
        const timeHint =
          status.color === 'info' && claim.validFrom
            ? ` (from ${formatDate(claim.validFrom)})`
            : status.color === 'error' && claim.validTo
              ? ` (expired ${formatDate(claim.validTo)})`
              : '';
        return (
          <Tooltip
            key={`${claim.brand}|${claim.code}`}
            title={`${claim.brand} · ${claim.code} — ${status.label}${timeHint}`}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                px: 0.75,
                height: 20,
                overflow: 'hidden',
                cursor: 'inherit',
              }}>
              <StatusIcon sx={{ fontSize: 12, color: iconColor, flexShrink: 0 }} />
              <Typography variant="caption" noWrap sx={{ fontSize: '0.875rem', lineHeight: 1 }}>
                {claim.brand}:{claim.code}
              </Typography>
            </Stack>
          </Tooltip>
        );
      })}
      {overflow > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}>
          +{overflow} more
        </Typography>
      )}
    </Box>
  );
};

// --- Expandable row ---

const NUM_COLS = 8;

const ACCENT_COLORS = ['#5c6bc0', '#00897b', '#8e24aa', '#0288d1', '#6d4c41', '#2e7d32'];

interface EntitlementRowProps {
  entitlement: Entitlement;
  identityId: string;
  onEdit: (data: EntitlementRowData) => void;
  onViewDetail: (event: IEmsAuditEvent) => void;
  isEditable: boolean;
  expanded: boolean;
  onToggle: () => void;
  accent: string;
  topDivider: boolean;
}

const EntitlementRow: FC<EntitlementRowProps> = ({
  entitlement,
  identityId,
  onEdit,
  onViewDetail,
  isEditable,
  expanded,
  onToggle,
  accent,
  topDivider,
}) => {
  const [tab, setTab] = useState(0);
  const [historyMounted, setHistoryMounted] = useState(false);
  const historyTabRef = useRef<HistoryTabHandle>(null);

  const { process, system, sourceId } = entitlement.id;
  const summary = getClaimsSummary(entitlement.claims);

  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
    if (newTab === 1) setHistoryMounted(true);
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'none' : undefined } }}
        onClick={onToggle}>
        <TableCell
          padding="checkbox"
          sx={{
            ...(topDivider && expanded && { borderTop: '2px solid rgba(0,0,0,0.2)' }),
            ...(expanded && { borderLeft: '3px solid', borderLeftColor: accent }),
          }}>
          <IconButton size="small">
            {expanded ? (
              <KeyboardArrowDownOutlinedIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <ClaimsQuickView claims={entitlement.claims} />
        </TableCell>
        <TableCell>
          <Typography variant="body2">{process ? toPascalCase(process) : '-'}</Typography>
        </TableCell>
        <TableCell>
          <Tooltip title={system ?? ''}>
            <Typography
              variant="body2"
              noWrap
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {system ?? '-'}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={sourceId ?? ''}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}>
              {sourceId ?? '-'}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {entitlement.roles && entitlement.roles.length > 0
              ? entitlement.roles.map((r) => (
                  <Chip key={r} label={r} size="small" variant="outlined" />
                ))
              : '-'}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap>
            {formatDate(entitlement.modificationTimestamp)}
          </Typography>
        </TableCell>
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          {isEditable && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEdit(mapToRowData(entitlement))}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={NUM_COLS}
          sx={{
            p: 0,
            border: expanded ? undefined : 'none',
            ...(expanded && { borderLeft: '3px solid', borderLeftColor: accent }),
          }}>
          <Collapse in={expanded}>
            <Box
              sx={{ backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
                textColor="primary"
                indicatorColor="primary">
                <Tab
                  sx={{ fontSize: '0.8rem', minHeight: 40 }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{`Claims (${summary.total})`}</span>
                      <Tooltip title="Full list of claims attached to this entitlement. Each claim shows its brand, code, and current status based on its validity dates.">
                        <InfoOutlinedIcon
                          sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Stack>
                  }
                />
                <Tab
                  sx={{ fontSize: '0.8rem', minHeight: 40 }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>History</span>
                      <Tooltip title="Audit log of changes to this entitlement. Shows who made each change, when, and what was modified.">
                        <InfoOutlinedIcon
                          sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
                        />
                      </Tooltip>
                      <Tooltip title="Reload history">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            historyTabRef.current?.refresh();
                          }}
                          sx={{ p: 0.25 }}>
                          <RefreshOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />
              </Tabs>

              {tab === 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <ClaimsTab claims={entitlement.claims} />
                </Box>
              )}
              {(tab === 1 || historyMounted) && (
                <Box sx={{ display: tab === 1 ? 'block' : 'none', overflow: 'hidden' }}>
                  <HistoryTab
                    ref={historyTabRef}
                    identityId={identityId}
                    process={process ?? ''}
                    system={system ?? ''}
                    sourceId={sourceId ?? ''}
                    onViewDetail={onViewDetail}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// --- Deleted entitlement row ---

const DeletedEntitlementRow: FC<{
  event: IEntitlementDeletedAuditEvent;
  identityId: string;
  onViewDetail: (event: IEmsAuditEvent) => void;
  isReAdded: boolean;
  onNavigateToCurrent: () => void;
  accent: string;
  topDivider: boolean;
}> = ({ event, identityId, onViewDetail, isReAdded, onNavigateToCurrent, accent, topDivider }) => {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState(0);
  const [historyMounted, setHistoryMounted] = useState(false);

  const snap = event.deletedEntitlement;
  const { process, system, sourceId } = snap ?? {};

  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
    if (newTab === 1) setHistoryMounted(true);
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'none' : undefined } }}
        onClick={() => setExpanded((p) => !p)}>
        <TableCell
          padding="checkbox"
          sx={{
            ...(topDivider && expanded && { borderTop: '2px solid rgba(0,0,0,0.2)' }),
            ...(expanded && { borderLeft: '3px solid', borderLeftColor: accent }),
          }}>
          <IconButton size="small">
            {expanded ? (
              <KeyboardArrowDownOutlinedIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <ClaimsQuickView claims={snap?.claims as Claim[] | undefined} />
        </TableCell>
        <TableCell>
          <Typography variant="body2">{process ? toPascalCase(process) : '-'}</Typography>
        </TableCell>
        <TableCell>
          <Tooltip title={system ?? ''}>
            <Typography
              variant="body2"
              noWrap
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {system ?? '-'}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title={sourceId ?? ''}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                }}>
                {sourceId ?? '-'}
              </Typography>
            </Tooltip>
            {isReAdded && (
              <Tooltip title="Currently active again — click to view">
                <Chip
                  label="Re-added ↗"
                  size="small"
                  color="success"
                  variant="filled"
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateToCurrent();
                  }}
                  sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {snap?.roles && snap.roles.length > 0
              ? snap.roles.map((r) => <Chip key={r} label={r} size="small" variant="outlined" />)
              : '-'}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap>
            {formatDate(snap?.modificationTimestamp)}
          </Typography>
        </TableCell>
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => onViewDetail(event)}>
              <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={8}
          sx={{
            p: 0,
            border: expanded ? undefined : 'none',
            ...(expanded && { borderLeft: '3px solid', borderLeftColor: accent }),
          }}>
          <Collapse in={expanded}>
            <Box
              sx={{ backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
                textColor="primary"
                indicatorColor="primary">
                <Tab
                  sx={{ fontSize: '0.8rem', minHeight: 40 }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>Snapshot</span>
                      <Tooltip title="The full state of this entitlement at the moment it was deleted.">
                        <InfoOutlinedIcon
                          sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Stack>
                  }
                />
                <Tab
                  sx={{ fontSize: '0.8rem', minHeight: 40 }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>History</span>
                      <Tooltip title="Audit log of all changes to this entitlement, including the deletion event.">
                        <InfoOutlinedIcon
                          sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Stack>
                  }
                />
              </Tabs>
              {tab === 0 && (
                <Box sx={{ p: 2 }}>
                  <EntitlementSnapshotView snapshot={snap} hideIdentity />
                </Box>
              )}
              {(tab === 1 || historyMounted) && snap && (
                <Box sx={{ display: tab === 1 ? 'block' : 'none', overflow: 'hidden' }}>
                  <HistoryTab
                    identityId={identityId}
                    process={snap.process ?? ''}
                    system={snap.system ?? ''}
                    sourceId={snap.sourceId ?? ''}
                    onViewDetail={onViewDetail}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// --- Deleted entitlements tab ---

interface DeletedEntitlementsTabHandle {
  refresh: () => void;
}

const DeletedEntitlementsTab = forwardRef<
  DeletedEntitlementsTabHandle,
  {
    identityId: string;
    currentEntitlements: Entitlement[];
    onViewDetail: (event: IEmsAuditEvent) => void;
    onNavigateToCurrent: (key: string) => void;
  }
>(({ identityId, currentEntitlements, onViewDetail, onNavigateToCurrent }, ref) => {
  const pagination = useCursorPagination(10);
  const { items, loading, error, hasNext, totalCount, refetch } = useKeysetData(
    () =>
      entitlementsService.getDeletedEntitlementEvents(
        identityId,
        pagination.pageSize,
        pagination.cursor
      ),
    [identityId, pagination.pageSize, pagination.cursor]
  );
  const [hideReAdded, setHideReAdded] = useState(true);

  // Only keep deletion events (API may return other types)
  const events = useMemo(
    () =>
      items.filter(
        (e) => e.type === 'auditing.event.entitlement.deleted.v1'
      ) as IEntitlementDeletedAuditEvent[],
    [items]
  );

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        pagination.resetPagination();
        refetch();
      },
    }),
    [pagination, refetch]
  );

  const currentKeys = useMemo(
    () =>
      new Set(
        currentEntitlements.map((e) => entitlementKey(e.id.process, e.id.system, e.id.sourceId))
      ),
    [currentEntitlements]
  );

  const visibleEvents = useMemo(
    () =>
      hideReAdded
        ? events.filter((e) => {
            const snap = e.deletedEntitlement;
            if (!snap) return true;
            return !currentKeys.has(entitlementKey(snap.process, snap.system, snap.sourceId));
          })
        : events,
    [events, hideReAdded, currentKeys]
  );

  const reAddedCount = events.length - visibleEvents.length;
  const from = events.length > 0 ? pagination.page * pagination.pageSize + 1 : 0;
  const to = pagination.page * pagination.pageSize + events.length;

  return (
    <Paper>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        px={2}
        py={0.75}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">
          Only shows entitlements deleted directly or via an all-claims-expired update after the
          introduction of entitlement audit logging.
        </Typography>
      </Stack>
      <TableContainer sx={{ overflow: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell padding="checkbox" sx={{ width: '1%' }} />
              <TableCell sx={{ width: '16%' }}>
                <ColHeader
                  label="Claims"
                  tip="Access grants that were attached to this entitlement at the time it was deleted."
                />
              </TableCell>
              <TableCell sx={{ width: '16%' }}>
                <ColHeader
                  label="Process"
                  tip="The business process that owned this entitlement (e.g. Subscription, Order)."
                />
              </TableCell>
              <TableCell sx={{ width: '16%' }}>
                <ColHeader label="System" tip="The source system that managed this entitlement." />
              </TableCell>
              <TableCell sx={{ width: '16%' }}>
                <ColHeader
                  label="Source ID"
                  tip="The external identifier used by the source system to reference this entitlement."
                />
              </TableCell>
              <TableCell sx={{ width: '16%' }}>
                <ColHeader
                  label="Roles"
                  tip="Roles that were granted to this identity through this entitlement."
                />
              </TableCell>
              <TableCell sx={{ width: '16%' }}>
                <ColHeader
                  label="Last modified"
                  tip="Timestamp of the last change before this entitlement was deleted."
                />
              </TableCell>
              <TableCell padding="checkbox" sx={{ width: '1%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {error && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="error" py={2}>
                    Failed to load deleted entitlements.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              ))}
            {!loading && !error && visibleEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    {events.length === 0
                      ? 'No deleted entitlements found.'
                      : 'All entries hidden (re-added). Toggle switch to show.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !error &&
              visibleEvents.map((event, index) => {
                const snap = event.deletedEntitlement;
                const key = snap ? entitlementKey(snap.process, snap.system, snap.sourceId) : null;
                const reAdded = key ? currentKeys.has(key) : false;
                return (
                  <DeletedEntitlementRow
                    key={event.id}
                    event={event}
                    identityId={identityId}
                    onViewDetail={onViewDetail}
                    isReAdded={reAdded}
                    onNavigateToCurrent={() => key && onNavigateToCurrent(key)}
                    accent={ACCENT_COLORS[index % ACCENT_COLORS.length]}
                    topDivider={index > 0}
                  />
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={0.75}
        sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideReAdded}
              onChange={(e) => setHideReAdded(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Hide re-added{reAddedCount > 0 && hideReAdded ? ` (${reAddedCount})` : ''}
            </Typography>
          }
          labelPlacement="start"
          sx={{ ml: 0, mr: 0, gap: 0.5 }}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            size="small"
            value={pagination.pageSize}
            onChange={(e) => pagination.handlePageSizeChange(Number(e.target.value))}
            sx={{ fontSize: '0.75rem', height: 28 }}>
            {PAGE_SIZE_OPTIONS.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>
                {s}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            {events.length > 0
              ? `${from}–${to} of ${totalCount}${reAddedCount > 0 && hideReAdded ? ` (${reAddedCount} hidden)` : ''}`
              : '—'}
          </Typography>
          <Button
            size="small"
            disabled={pagination.page === 0}
            onClick={() => pagination.handlePageChange(pagination.page - 1, events)}
            startIcon={<NavigateBeforeOutlinedIcon />}
            sx={{ minWidth: 0, px: 1 }}>
            Prev
          </Button>
          <Button
            size="small"
            disabled={!hasNext}
            onClick={() => pagination.handlePageChange(pagination.page + 1, events)}
            endIcon={<NavigateNextOutlinedIcon />}
            sx={{ minWidth: 0, px: 1 }}>
            Next
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
});
DeletedEntitlementsTab.displayName = 'DeletedEntitlementsTab';

// --- Main component ---

const EntitlementsEnhancedDataGrid: FC<EntitlementsEnhancedDataGridProps> = ({
  collectionHook,
  onEdit,
  identityId,
  onRefreshCurrent,
}) => {
  const { collection, hasErrors, isLoading } = collectionHook;
  const { roles } = useAuth();
  const [topTab, setTopTab] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailEvent, setDetailEvent] = useState<IEmsAuditEvent | null>(null);
  const [filters, setFilters] = useState<{
    claims: string[];
    process: string;
    system: string;
    sourceId: string;
    roles: string;
  }>({
    claims: [],
    process: '',
    system: '',
    sourceId: '',
    roles: '',
  });
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);

  const deletedTabRef = useRef<DeletedEntitlementsTabHandle>(null);

  const setFilter = useCallback((key: keyof typeof filters, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);

  const handleViewDetail = useCallback((event: IEmsAuditEvent) => setDetailEvent(event), []);
  const handleCloseDetail = useCallback(() => setDetailEvent(null), []);
  const handleToggleRow = useCallback((key: string) => {
    setExpandedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const handleNavigateToCurrent = useCallback((key: string) => {
    setExpandedRowKeys((prev) => new Set(Array.from(prev).concat(key)));
    setTopTab(0);
  }, []);

  const entitlements = useMemo(
    () =>
      [...(collection ?? [])].sort(
        (a, b) =>
          new Date(b.modificationTimestamp).getTime() - new Date(a.modificationTimestamp).getTime()
      ),
    [collection]
  );

  const claimOptions = useMemo(() => {
    const seen = new Set<string>();
    (collection ?? []).forEach((e) =>
      (e.claims ?? []).forEach((c) => {
        if (c.brand && c.code) seen.add(`${c.brand}:${c.code}`);
      })
    );
    return Array.from(seen).sort();
  }, [collection]);

  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    (collection ?? []).forEach((e) => (e.roles ?? []).forEach((r) => seen.add(r)));
    return Array.from(seen).sort();
  }, [collection]);

  if (hasErrors)
    return <TableErrorComponent message="An error occurred, data could not be loaded." />;

  const lc = (s: string) => s.toLowerCase();
  const filtered = entitlements.filter((e) => {
    if (filters.claims.length > 0) {
      const claimKeys = new Set((e.claims ?? []).map((c) => `${c.brand}:${c.code}`));
      if (!filters.claims.some((sel) => claimKeys.has(sel))) return false;
    }
    if (filters.process && lc(e.id.process ?? '') !== lc(filters.process)) return false;
    if (filters.system && !lc(e.id.system ?? '').includes(lc(filters.system))) return false;
    if (filters.sourceId && !lc(e.id.sourceId ?? '').includes(lc(filters.sourceId))) return false;
    if (filters.roles && !(e.roles ?? []).some((r) => lc(r) === lc(filters.roles))) return false;
    if (dateFrom || dateTo) {
      const ts = dayjs(e.modificationTimestamp);
      if (dateFrom && ts.isBefore(dateFrom.startOf('day'))) return false;
      if (dateTo && ts.isAfter(dateTo.endOf('day'))) return false;
    }
    return true;
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Paper>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={topTab}
            onChange={(_, v) => setTopTab(v)}
            sx={{ px: 1 }}
            textColor="primary"
            indicatorColor="primary">
            <Tab
              sx={{ fontSize: '0.85rem', minHeight: 44 }}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>Current</span>
                  <Tooltip
                    title="Active entitlements currently assigned to this identity. Click a row to expand claims and change history."
                    placement="top">
                    <InfoOutlinedIcon
                      sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
                    />
                  </Tooltip>
                </Stack>
              }
            />
            <Tab
              sx={{ fontSize: '0.85rem', minHeight: 44 }}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>Deleted</span>
                  <Tooltip
                    title="Deleted entitlements — either explicitly or via an update that expired all claims. Only available from the date audit logging was introduced."
                    placement="top">
                    <InfoOutlinedIcon
                      sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
                    />
                  </Tooltip>
                </Stack>
              }
            />
          </Tabs>
          <Tooltip
            title={topTab === 0 ? 'Refresh current entitlements' : 'Refresh deleted entitlements'}>
            <IconButton
              size="small"
              onClick={topTab === 0 ? onRefreshCurrent : () => deletedTabRef.current?.refresh()}>
              <RefreshOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {topTab === 0 && (
          <>
            <TableContainer sx={{ overflow: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell padding="checkbox" sx={{ width: '1%' }} />
                    <TableCell sx={{ width: '16%' }}>
                      <ColHeader
                        label="Claims"
                        tip="Access grants attached to this entitlement. Each claim has a brand, code, and optional validity window."
                      />
                    </TableCell>
                    <TableCell sx={{ width: '16%' }}>
                      <ColHeader
                        label="Process"
                        tip="The business process that owns this entitlement (e.g. Subscription, Order)."
                      />
                    </TableCell>
                    <TableCell sx={{ width: '16%' }}>
                      <ColHeader
                        label="System"
                        tip="The source system that manages this entitlement."
                      />
                    </TableCell>
                    <TableCell sx={{ width: '16%' }}>
                      <ColHeader
                        label="Source ID"
                        tip="The external identifier used by the source system to reference this entitlement."
                      />
                    </TableCell>
                    <TableCell sx={{ width: '16%' }}>
                      <ColHeader
                        label="Roles"
                        tip="Additional roles granted to this identity through this entitlement."
                      />
                    </TableCell>
                    <TableCell sx={{ width: '16%' }}>
                      <ColHeader
                        label="Last modified"
                        tip="Timestamp of the most recent change to this entitlement."
                      />
                    </TableCell>
                    <TableCell padding="checkbox" sx={{ width: '1%' }} />
                  </TableRow>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ py: 0.5 }}>
                      <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={filters.claims}
                        onChange={(e) => setFilter('claims', e.target.value as string[])}
                        renderValue={(selected) =>
                          selected.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">
                              All claims
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.25} flexWrap="wrap" rowGap={0.25}>
                              {selected.map((s) => (
                                <Chip
                                  key={s}
                                  label={s}
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.65rem' }}
                                />
                              ))}
                            </Stack>
                          )
                        }
                        sx={{ fontSize: '0.75rem', minHeight: 28, width: '100%' }}>
                        {claimOptions.map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.75rem' }}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Select
                        size="small"
                        displayEmpty
                        value={filters.process}
                        onChange={(e) => setFilter('process', e.target.value)}
                        sx={{ fontSize: '0.75rem', height: 28, width: '100%' }}>
                        <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                          <Typography variant="caption" color="text.disabled">
                            All
                          </Typography>
                        </MenuItem>
                        {Object.values(EntitlementProcess).map((p) => (
                          <MenuItem key={p} value={p} sx={{ fontSize: '0.75rem' }}>
                            {toPascalCase(p)}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    {(
                      [
                        ['system', filters.system],
                        ['sourceId', filters.sourceId],
                      ] as [keyof typeof filters, string][]
                    ).map(([key, value]) => (
                      <TableCell key={key} sx={{ py: 0.5 }}>
                        <OutlinedInput
                          size="small"
                          value={value}
                          onChange={(e) => setFilter(key, e.target.value)}
                          placeholder="Filter…"
                          endAdornment={
                            value ? (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setFilter(key, '')}>
                                  <ClearOutlinedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </InputAdornment>
                            ) : undefined
                          }
                          sx={{ fontSize: '0.75rem', height: 28, width: '100%' }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ py: 0.5 }}>
                      <Select
                        size="small"
                        displayEmpty
                        value={filters.roles}
                        onChange={(e) => setFilter('roles', e.target.value)}
                        sx={{ fontSize: '0.75rem', height: 28, width: '100%' }}>
                        <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                          <Typography variant="caption" color="text.disabled">
                            All
                          </Typography>
                        </MenuItem>
                        {roleOptions.map((r) => (
                          <MenuItem key={r} value={r} sx={{ fontSize: '0.75rem' }}>
                            {r}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={(e) => setDateAnchorEl(e.currentTarget)}
                        startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 12 }} />}
                        endIcon={
                          dateFrom || dateTo ? (
                            <ClearOutlinedIcon
                              sx={{ fontSize: 12 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDateFrom(null);
                                setDateTo(null);
                                setPage(0);
                              }}
                            />
                          ) : undefined
                        }
                        sx={{
                          fontSize: '0.7rem',
                          height: 28,
                          width: '100%',
                          px: 0.75,
                          ...(dateFrom || dateTo
                            ? { backgroundColor: 'action.selected', borderColor: 'text.secondary' }
                            : {}),
                        }}>
                        {dateFrom || dateTo
                          ? `${dateFrom ? dateFrom.format('DD/MM/YY') : '…'} – ${dateTo ? dateTo.format('DD/MM/YY') : '…'}`
                          : 'All dates'}
                      </Button>
                      <Popover
                        open={Boolean(dateAnchorEl)}
                        anchorEl={dateAnchorEl}
                        onClose={() => setDateAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Stack spacing={2} sx={{ p: 2 }}>
                            <DatePicker
                              label="From"
                              value={dateFrom}
                              onChange={(val) => {
                                setDateFrom(val);
                                setPage(0);
                              }}
                              slotProps={{ textField: { size: 'small' } }}
                            />
                            <DatePicker
                              label="To"
                              value={dateTo}
                              onChange={(val) => {
                                setDateTo(val);
                                setPage(0);
                              }}
                              slotProps={{ textField: { size: 'small' } }}
                            />
                          </Stack>
                        </LocalizationProvider>
                      </Popover>
                    </TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading &&
                    [1, 2, 3].map((i) => (
                      <TableRow key={i}>
                        <TableCell padding="checkbox" />
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" />
                        </TableCell>
                        <TableCell padding="checkbox" />
                      </TableRow>
                    ))}
                  {!isLoading && paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={NUM_COLS} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No entitlements found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading &&
                    paginated.map((entitlement, index) => {
                      const { process, system, sourceId } = entitlement.id;
                      const rowKey = entitlementKey(process, system, sourceId);
                      const rowData = mapToRowData(entitlement);
                      return (
                        <EntitlementRow
                          key={rowKey}
                          entitlement={entitlement}
                          identityId={identityId}
                          onEdit={onEdit}
                          onViewDetail={handleViewDetail}
                          isEditable={isEditableEntitlement(rowData, roles)}
                          expanded={expandedRowKeys.has(rowKey)}
                          onToggle={() => handleToggleRow(rowKey)}
                          accent={ACCENT_COLORS[index % ACCENT_COLORS.length]}
                          topDivider={index > 0}
                        />
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            {filtered.length > 5 && (
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            )}
          </>
        )}

        {topTab === 1 && (
          <DeletedEntitlementsTab
            ref={deletedTabRef}
            identityId={identityId}
            currentEntitlements={entitlements}
            onViewDetail={handleViewDetail}
            onNavigateToCurrent={handleNavigateToCurrent}
          />
        )}
      </Paper>

      <EntitlementEventDetailDialog event={detailEvent} onClose={handleCloseDetail} />
    </>
  );
};

export default EntitlementsEnhancedDataGrid;
